/* eslint-disable @typescript-eslint/camelcase */
import {
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  Switch,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import Button from 'components/Button';
import Loading from 'components/Loading';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import { getObjectsAvailableRequest } from 'modules/permission/actions';
import {
  ListObjectPartnerDataProps as ListObjectProps,
  ObjectPartnerProps as ObjectProps,
} from 'modules/permission/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState, { ListDataProps } from 'store/interfaces';
import { useTableFilters } from 'hooks/useTableFilters';
import { initialQuery } from '..';
import styles from '../../Template/styles';
import ActionSearch from '../ActionSearch';
import { ENTITY_NAME } from './Content';

const useStyles = makeStyles(styles);

interface Props {
  id: string;
  items: {
    loading: boolean;
    selected: ListObjectProps;
    available: ListObjectProps;
  };
  pending: ObjectProps[];
  allSelected: boolean;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: (values: ObjectProps[], allSelected: boolean) => void;
  getObjectsAvailableRequest: (props: {
    role_uuid: string;
    entity: string;
    query?: any;
  }) => Promise<void>;
}

const ListAvailable = ({
  id,
  items,
  pending,
  allSelected: allSelectedValue,
  isLoading,
  onCancel,
  onConfirm,
  getObjectsAvailableRequest,
}: Props) => {
  const classes = useStyles();
  const {
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
  } = useTableFilters(initialQuery);

  const [data, setData] = React.useState<ListDataProps | null>(null);
  const [checked, setChecked] = React.useState<string[]>(
    pending.map(({ uuid }) => uuid)
  );
  const [allSelected, setAllSelected] = React.useState(allSelectedValue);
  const [pendingList, setPendingList] = React.useState<ObjectProps[]>(pending);

  const handleCheck = {
    exists: (item: ObjectProps) => checked.includes(item?.uuid),
    toggle: (item: ObjectProps) => {
      if (pendingList.find(i => i.uuid === item?.uuid)) {
        setChecked(prev => prev.filter(i => i !== item?.uuid));
        setPendingList(prev => prev.filter(i => i.uuid !== item?.uuid));
      } else {
        setChecked(prev => [...prev, item?.uuid]);
        setPendingList(prev => [...prev, item]);
      }
    },
    toggleAll: (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setChecked(data?.results.map(({ uuid }) => uuid) || []);
        setPendingList(data?.results || []);
      } else {
        setChecked([]);
        setPendingList([]);
      }
    },
  };

  const handleConfirm = () => onConfirm(pendingList, allSelected);

  React.useEffect(() => {
    getObjectsAvailableRequest({ role_uuid: id, entity: ENTITY_NAME, query });
  }, [getObjectsAvailableRequest, id, query]);

  React.useEffect(() => {
    if (items?.available) {
      setData(items.available);
    }
  }, [items]);

  if (!data) {
    return <Loading />;
  }

  return (
    <React.Fragment>
      <Grid item xs={12} className={classes.switchModal}>
        <FormControlLabel
          control={
            <Switch
              id="select-all"
              checked={allSelected}
              onChange={() => setAllSelected(prev => !prev)}
              size="small"
            />
          }
          label={i18n.t('SELECT_ALL_PARTNERS_KEY')}
        />
      </Grid>
      <br />
      {allSelected ? (
        <Alert severity="info">{i18n.t('INFO_AUTOMATICALLY_ADDED_KEY')}</Alert>
      ) : (
        <Grid item xs={12}>
          <ActionSearch
            id={`search-${ENTITY_NAME}`}
            label={i18n.t('SEARCH_PARTNER_KEY')}
            onSearch={handleSearch}
            isLoading={items?.loading || isLoading}
          />
        </Grid>
      )}
      <br />
      <Grid className={classes.minimalTable}>
        {!allSelected && (
          <Grid className={classes.table}>
            <TableItems
              overrideRowsCount={10}
              header={
                <>
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox onChange={handleCheck.toggleAll} />
                  </TableCell>
                  <TableCell
                    order
                    className={classes.header}
                    handleOrder={() => handleOrder('nome')}
                  >
                    {i18n.t('PARTNER_NAME_KEY')}
                  </TableCell>
                  <TableCell
                    order
                    className={classes.header}
                    handleOrder={() => handleOrder('alias')}
                  >
                    Alias
                  </TableCell>
                  <TableCell
                    className={classes.header}
                    order
                    handleOrder={() => handleOrder('company_name')}
                  >
                    {i18n.t('COMPANY_KEY')}
                  </TableCell>
                </>
              }
              renderItems={(item: ObjectProps) => (
                <TableRow
                  key={item?.uuid}
                  className={clsx({
                    [classes.pending]: handleCheck.exists(item),
                  })}
                >
                  <TableCell
                    className={classes.checkboxCell}
                    style={{ width: 20 }}
                  >
                    <Checkbox
                      onChange={() => handleCheck.toggle(item)}
                      checked={handleCheck.exists(item)}
                    />
                  </TableCell>
                  <TableCell>{item?.name}</TableCell>
                  <TableCell>{item?.alias}</TableCell>
                  <TableCell>{item?.company?.name}</TableCell>
                </TableRow>
              )}
              data={items.available}
              isLoading={items?.loading}
              onChangePage={handlePageChange}
              onChangeLimit={handleLimitChange}
              notFoundText={i18n.t('NO_PARTNER_KEY')}
              colCount={5}
            />
          </Grid>
        )}

        <Grid container justify="flex-end" className={classes.buttonsBase}>
          <Grid container className={classes.buttonsAlign}>
            <Button submitButton id="submit-button" onClick={handleConfirm}>
              {i18n.t('ADD_KEY')}
            </Button>
            <Button id="cancel-button" variant="outlined" onClick={onCancel}>
              {i18n.t('CANCEL_KEY')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  permission: { objects, isLoading },
}: ApplicationState) => ({
  items: objects[ENTITY_NAME],
  isLoading,
});

export default connect(mapStateToProps, {
  getObjectsAvailableRequest,
})(ListAvailable);
